import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { useEnergyTimeSeries } from '@/hooks/useEnergyTimeSeries';
import type { CompareSlot, TimeGranularity, GroupTimeSeriesPoint, MetricKey } from '@/types/reports';
import { DEFAULT_METRIC_TOGGLES } from '@/types/reports';
import type { Group, IndustryType } from '@/types';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';

interface CompareChartSectionProps {
  slot: CompareSlot;
  dateRange: { start: Date | null; end: Date | null } | null;
  granularity: TimeGranularity;
  groups: Group[];
  selectedIndustryTypes: IndustryType[];
  enabledMetrics?: MetricKey[];
}

// Map MetricKey to data accessor for time series points
const getMetricValue = (point: GroupTimeSeriesPoint, key: MetricKey): number => {
  switch (key) {
    case 'solarKwh':
      return point.solarKwh || 0;
    case 'gridKwh':
      return point.gridKwh || 0;
    case 'offGridPercent':
      return point.offGridPercent || 0;
    case 'co2Reduced':
      return point.co2Reduced || 0;
    case 'costSaved':
      return point.costSaved || 0;
    case 'gridSellRevenue':
      return point.gridSellRevenue || 0;
    default:
      return 0;
  }
};

export function CompareChartSection({
  slot,
  dateRange,
  granularity,
  groups: _groups,
  selectedIndustryTypes,
  enabledMetrics = ['solarKwh'],
}: CompareChartSectionProps) {
  // Fetch time series data for this slot
  const { timeSeries: timeSeriesData, isLoading } = useEnergyTimeSeries({
    groupIds: slot.groupIds,
    startDate: dateRange?.start || null,
    endDate: dateRange?.end || null,
    granularity,
    modelCodes: slot.modelCodes,
    industryTypes: selectedIndustryTypes,
    enabled: slot.groupIds.length > 0 && !!dateRange,
  });

  // Get metric configs for enabled metrics
  const metricConfigs = useMemo(() => {
    return enabledMetrics
      .map(key => DEFAULT_METRIC_TOGGLES.find(m => m.key === key))
      .filter((m): m is typeof DEFAULT_METRIC_TOGGLES[0] => !!m);
  }, [enabledMetrics]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!timeSeriesData || timeSeriesData.length === 0) {
      return { categories: [], series: [], hasPercentMetric: false };
    }

    const categories = timeSeriesData.map((point: GroupTimeSeriesPoint) => {
      // Use periodLabel directly since it's already formatted
      return point.periodLabel;
    });

    // Check if we have any percent metrics (for dual-axis)
    const hasPercentMetric = metricConfigs.some(m => m.yAxisGroup === 'percent');

    // Create series for each enabled metric
    const series = metricConfigs.map(config => ({
      name: config.label,
      data: timeSeriesData.map((point: GroupTimeSeriesPoint) => getMetricValue(point, config.key)),
    }));

    return { categories, series, hasPercentMetric };
  }, [timeSeriesData, metricConfigs]);

  // Get colors for enabled metrics - use slot color for first metric, then metric-specific colors
  const chartColors = useMemo(() => {
    if (metricConfigs.length === 0) return [slot.color];
    // For single metric, use slot color; for multiple metrics, use metric colors
    if (metricConfigs.length === 1) return [slot.color];
    return metricConfigs.map(m => m.color);
  }, [metricConfigs, slot.color]);

  const chartOptions: ApexOptions = useMemo(() => ({
    chart: {
      id: `compare-chart-${slot.id}`,
      type: 'area',
      toolbar: { show: false },
      sparkline: { enabled: false },
      background: 'transparent',
      animations: {
        enabled: false, // Disable animations to prevent scroll flicker
      },
      redrawOnParentResize: true, // Enable redraw on parent resize to handle scroll-induced layout changes
      redrawOnWindowResize: true,
    },
    colors: chartColors,
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 100],
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: chartData.categories,
      labels: {
        style: {
          colors: 'hsl(var(--muted-foreground))',
          fontSize: '10px',
        },
        rotate: -45,
        rotateAlways: chartData.categories.length > 7,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: chartData.hasPercentMetric ? [
      // Primary Y-axis for absolute values
      {
        labels: {
          style: {
            colors: 'hsl(var(--muted-foreground))',
            fontSize: '10px',
          },
          formatter: (val) => val.toLocaleString(undefined, { maximumFractionDigits: 0 }),
        },
      },
      // Secondary Y-axis for percentage
      {
        opposite: true,
        max: 100,
        min: 0,
        labels: {
          style: {
            colors: 'hsl(var(--muted-foreground))',
            fontSize: '10px',
          },
          formatter: (val) => `${val}%`,
        },
      },
    ] : {
      labels: {
        style: {
          colors: 'hsl(var(--muted-foreground))',
          fontSize: '10px',
        },
        formatter: (val) => val.toLocaleString(undefined, { maximumFractionDigits: 0 }),
      },
    },
    grid: {
      borderColor: 'hsl(var(--border))',
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { left: 10, right: 10 },
    },
    tooltip: {
      theme: 'dark',
      y: {
        formatter: (val, { seriesIndex }) => {
          const metric = metricConfigs[seriesIndex];
          if (!metric) return `${val.toLocaleString()}`;
          const unit = metric.unit;
          if (unit === '%') return `${val.toFixed(1)}%`;
          if (unit === '$') return `$${val.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
          return `${val.toLocaleString()} ${unit}`;
        },
      },
    },
    legend: {
      show: metricConfigs.length > 1,
      position: 'top',
      horizontalAlign: 'center',
      fontSize: '10px',
      labels: {
        colors: 'hsl(var(--muted-foreground))',
      },
      markers: {
        size: 8,
        offsetX: -4,
      },
      itemMargin: {
        horizontal: 12,
      },
    },
  }), [slot.id, chartData.categories, chartData.hasPercentMetric, chartColors, metricConfigs]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (chartData.series.length === 0 || chartData.categories.length === 0) {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px] text-sm text-muted-foreground">
        No data available for selected period
      </div>
    );
  }

  return (
    <div
      className="h-[300px] overflow-visible relative"
      style={{
        isolation: 'isolate',
        contain: 'layout style',
      }}
    >
      <Chart
        options={chartOptions}
        series={chartData.series}
        type="area"
        height="100%"
      />
    </div>
  );
}
